import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import ProSelectField from "../../Components/Inputs/SelectField";
import DateField from "../../Components/Inputs/DateField";
import { useForm } from "react-hook-form";
import { ADD_DAVIS_BACON_FORM, APPRENTICES_BY_CLIENT } from "../../../utils/services/apiPath";
import { getApi, postApi } from "../../../utils/services";
import { convertFileToBase64Model, formatPayloadDate, prepareDefaultValues, prepareInitialConfig } from "../../../utils/formHelper";
import { Box, Button, Grid, IconButton, Modal, Typography } from "@mui/material";
import { Close, FileUpload } from "@mui/icons-material";
import fileUpload from "../../Assets/Icons/FileUpload.png";
import ProButton from "../ProButton";
import FileUploadBox from "./FileUploadBox";

const uploadPopupContainer = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4
};

const davisBaconFormFields = {
  apprenticeId: {
    label: "Apprentice Name",
    perKey: "Apprentice",
    placeholder: "Select Apprentice",
    rules: { required: "Apprentice Name is Required!" }
  },
  certificateIssueDate: {
    label: "Certificate Issue Date",
    perKey: "certificateIssueDate",
    defaultValue: null,
    placeholder: "Select Certificate Issue Date",
    rules: { required: "Certificate Issue Date is Required!" }
  }
};

export default function UploadFileDavisBacon({ title, getDetailData, fetchInsightData, projectId, partnerId }) {
  const [formConfig] = useState(prepareInitialConfig(davisBaconFormFields, "edit"));
  const { handleSubmit, control, watch } = useForm({ defaultValues: prepareDefaultValues(davisBaconFormFields) });
  const [apprenticeList, setApprenticeList] = useState([]);
  const [file, setFile] = useState(null);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const apprentice = watch("apprenticeId");
  const certificateIssueDate = watch("certificateIssueDate");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);


  const handleFileSelect = selectedFile => {
    setErrorMessage("");
    setFile(selectedFile);
  };

  const fetchApprentices = useCallback(async () => {
    const params = new URLSearchParams({ includeAll: true });
    if (projectId) params.append("projectId", projectId);
    if (partnerId) params.append("partnerId", partnerId);
    const res = await getApi(`${APPRENTICES_BY_CLIENT}?${params.toString()}`);
    if (Array.isArray(res)) {
      setApprenticeList(res.map(item => ({ label: item.apprenticeName, value: item.apprenticeId })));
    }
  }, [projectId, partnerId]);

  useEffect(() => {
    fetchApprentices();
  }, [fetchApprentices]);

  const defaultFormProps = { control };

  const handleSave = async () => {
    if (!file) {
      setErrorMessage("Davis-Bacon Form is Required!");
      return;
    }
    const payload = {
      employeeId: apprentice,
      issuedDate: formatPayloadDate(certificateIssueDate),
      documentMediaModel: await convertFileToBase64Model(file)
    };
    await postApi(ADD_DAVIS_BACON_FORM, payload);
    getDetailData();
    fetchInsightData();
    handleClose();
  };

  return (
    <>
      <Button id="upload-files" variant="outlined" size="small" sx={{ width: "150px" }} onClick={handleOpen}>
        Upload
        <FileUpload />
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Box sx={uploadPopupContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" component="h2">
                {title}
              </Typography>
              <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 16, top: 16 }}>
                <Close />
              </IconButton>
            </Grid>
            <ProSelectField
              {...defaultFormProps}
              {...formConfig.apprenticeId}
              options={apprenticeList}
              permission={{ canView: true, canAdd: true, canEdit: true }}
            />
            <DateField {...defaultFormProps} {...formConfig.certificateIssueDate} permission={{ canView: true, canAdd: true, canEdit: true }} />
            <Grid item xs={12}>
              <FileUploadBox onFileSelect={handleFileSelect} fileUpload={fileUpload} file={file} errorMessage={errorMessage} />
            </Grid>

            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}>
              <ProButton variant="outlined" color="secondary" onClick={handleClose}>
                {"Cancel"}
              </ProButton>
              <ProButton onClick={handleSubmit(handleSave)}>{"Save"}</ProButton>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
}

UploadFileDavisBacon.propTypes = {
  title: PropTypes.string,
  getDetailData: PropTypes.func,
  fetchInsightData: PropTypes.func,
  projectId: PropTypes.string,
  partnerId: PropTypes.string
};